import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Play, Plus, Trash2 } from 'lucide-react';
import { GAME_MODULES } from './gameModules';
import './GameMenu.css';
import { getCurrentUser } from 'aws-amplify/auth';

export const WINTERMERE_ID = 'wintermere';
const client = generateClient();

const GameMenu = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const session = await fetchAuthSession();
      const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
      setIsAdmin(groups.includes('admin'));
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    }
  };

  const handlePlayClick = (gameId) => {
    navigate(`/party/${gameId}/selection`);
  };

  const handleCreateWintermere = async () => {
    try {
      setCreating(true);
      await client.graphql({
        query: `
          mutation CreateGameModule($input: CreateGameModuleInput!) {
            createGameModule(input: $input) {
              id
              name
              description
            }
          }
        `,
        variables: {
          input: {
            id: 'wintermere',
            name: 'Frost and Bones: The Ruins of Castle Wintermere',
            description: 'Embark on a chilling adventure in a frost-covered land.',
            scenes: JSON.stringify({
              intro: {
                id: "intro_01",
                title: "The Frozen Gates",
                description: "The bitter wind howls around you...",
                choices: []
              }
            }),
            recommendedPlayers: 1,
            minLevel: 1,
            maxLevel: 5,
            maxPartySize: 1,
            startingLevel: 1,
            customSettings: null,
            difficulty: 'EASY',
            estimatedPlayTime: "2-3 hours",
            tags: ['Single Player', 'Story Rich', 'Fantasy'],
            version: "1.0.0",
            status: 'PUBLISHED',
            creatorId: 'system'
          }
        }
      });

      alert('Wintermere module created successfully!');
      window.location.reload();

    } catch (error) {
      console.error('Failed to create Wintermere module:', error);
      alert('Failed to create Wintermere module: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) {
      return;
    }

    try {
      setDeleting(true);
      await client.graphql({
        query: `
          mutation DeleteGameModule($input: DeleteGameModuleInput!) {
            deleteGameModule(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            id: moduleId
          }
        }
      });

      alert('Module deleted successfully!');
      window.location.reload();

    } catch (error) {
      console.error('Failed to delete module:', error);
      alert('Failed to delete module: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="game-menu">
      <div className="game-menu__header">
        <h1 className="game-menu__title">Available Games</h1>
        {isAdmin && process.env.NODE_ENV === 'development' && (
          <div className="game-menu__admin-controls">
            <button 
              onClick={handleCreateWintermere}
              className="game-menu__admin-button"
              disabled={creating}
            >
              <Plus className="h-4 w-4 mr-2" />
              {creating ? 'Creating...' : 'Create Wintermere'}
            </button>
          </div>
        )}
      </div>
      <div className="game-grid">
        {GAME_MODULES.map((game) => (
          <div key={game.id} className="game-card">
            <div className="game-card__image-container">
              {game.thumbnail}
            </div>
            <div className="game-card__content">
              <h2 className="game-card__title">{game.name}</h2>
              <p className="game-card__description">{game.description}</p>
              <p className="game-card__players">
                Players: {game.minPlayers}-{game.maxPlayers}
              </p>
              <p className="game-card__level">
                Levels: {game.minLevel}-{game.maxLevel}
              </p>
              <div className="game-card__actions">
                <button 
                  onClick={() => handlePlayClick(game.id)} 
                  className="game-card__button"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Play Now
                </button>
                {isAdmin && (
                  <button 
                    onClick={() => handleDeleteModule(game.id)}
                    className="game-card__admin-button game-card__admin-button--delete"
                    disabled={deleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameMenu;